import React, { useEffect, useState } from "react";
import { useJsApiLoader, GoogleMap, Marker, DirectionsRenderer } from '@react-google-maps/api';
import { useParams, useNavigate } from "react-router-dom";
import MetaData from '../layout/MetaData';
import { useOrderDetailsQuery, useDeleteMyOrderMutation, useFinishBookingUserMutation } from '../../redux/api/bookingApi';
import Loader from '../layout/Loader';
import toast from 'react-hot-toast';
import { ProgressBar } from 'react-bootstrap';
import '../../css/orders.css';

const statusSteps = [
  'Vehicle not dispatched yet',
  'Vehicle Dispatched',
  'Vehicle Arrived At pickup location',
  'Vehicle has left from pick up location',
  'Vehicle Arrived At Destination location',
];

const OrderDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [markerVisible, setMarkerVisible] = useState(false);
  const [map, setMap] = useState(null);

  const { data, isLoading, error } = useOrderDetailsQuery(params?.id, { pollingInterval: 10000 });
  const order = data?.order || {};
  const { driverLocation, driver, userAddressInfo, labour, googleMap, goodsInfo, user, totalAmount, orderStatus, userRecieved } = order;

  const [deleteMyOrder, { error: deleteError, isLoading: isDeleteLoading, isSuccess }] = useDeleteMyOrderMutation();
  const [finishBookingUser, { isLoading: isFinishLoading, isSuccess: isFinishSuccess, error: finishError }] = useFinishBookingUserMutation();

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY, libraries: ['places'] });

  useEffect(() => {
    if (error) toast.error(error?.data?.message);
    if (deleteError) toast.error(deleteError?.data?.message);
    if (isSuccess) { toast.success("Booking Cancelled"); navigate("/"); }
  }, [error, deleteError, isSuccess]);

  useEffect(() => {
    if (finishError) toast.error(finishError?.data?.message || "Failed to finish booking");
    if (isFinishSuccess) toast.success("Booking finished successfully!");
  }, [finishError, isFinishSuccess]);

  useEffect(() => {
    const timer = setTimeout(() => setMarkerVisible(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading || !isLoaded) return <Loader />;

  const deleteOrderHandler = () => deleteMyOrder(order?._id);
  const center = { lat: driverLocation?.latitude || 0, lng: driverLocation?.longitude || 0 };
  const getCurrentStep = () => { const i = statusSteps.indexOf(orderStatus); return i === -1 ? 0 : i; };
  const progress = (getCurrentStep() / (statusSteps.length - 1)) * 100;

  const TableRow = ({ label, value }) => (
    <tr><th scope="row">{label}</th><td>{value || '—'}</td></tr>
  );

  return (
    <>
      <MetaData title="Booking Details" />
      <div className="row d-flex justify-content-center" style={{ padding: '1rem' }}>
        <div className="col-12 col-lg-9 mt-4 order-details">

          {/* Finding Driver State */}
          {orderStatus === "Finding Driver" && (
            <div className="card p-5 text-center mb-4">
              <h3 className="mb-3">Finding a Driver For You...</h3>
              <div className="loader-booking-detail"><Loader /></div>
              <button className="btn btn-outline-danger mt-4" onClick={deleteOrderHandler} disabled={isDeleteLoading} id="delete-btn-mybooking">
                Cancel Booking
              </button>
            </div>
          )}

          {/* Progress Tracker */}
          {orderStatus !== "Finding Driver" && (
            <div className="card p-4 mb-4 orderstatusshowerpagetop">
              <h4 className="mb-3 text-center">Booking Progress</h4>
              <ProgressBar now={progress} label={`${Math.round(progress)}%`} />
              <div className="d-flex flex-wrap justify-content-between mt-3 text-muted" style={{ fontSize: '0.7rem' }}>
                {statusSteps.map((step, i) => (
                  <div key={i} className={`text-center ${i <= getCurrentStep() ? "fw-bold text-success" : ""}`} style={{ flex: '1 1 18%', minWidth: '60px' }}>
                    {step}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Completed */}
          {orderStatus === "Job Completed Successfully" && (
            <div className="card p-4 mb-4 text-center">
              <div className="progress" style={{ height: '8px' }}>
                <div className="progress-bar bg-success" style={{ width: '100%' }} />
              </div>
              <p className="mt-2 text-success fw-bold">Job Completed ✅</p>
            </div>
          )}

          {/* Driver Map */}
          {orderStatus !== "Finding Driver" && orderStatus !== "Vehicle not dispatched yet" && orderStatus !== "Vehicle Dispatched" && orderStatus !== "Job Completed Successfully" && orderStatus !== "Booking Canceled" && driverLocation?.latitude && (
            <div className="card p-4 mb-4">
              <h4 className="mb-3">Driver Live Location</h4>
              <GoogleMap className="mapofgoogleinusersbookings" center={center} zoom={18} mapContainerStyle={{ width: '100%', height: '45vh', borderRadius: '12px', overflow: 'hidden' }} options={{ zoomControl: false, streetViewControl: false, mapTypeControl: false, fullscreenControl: false, disableDefaultUI: true }} onLoad={m => setMap(m)}>
                {markerVisible && <Marker position={center} icon={{ url: "http://maps.google.com/mapfiles/ms/icons/truck.png", scaledSize: new window.google.maps.Size(40, 40) }} />}
              </GoogleMap>
            </div>
          )}

          {/* Finish Button */}
          {orderStatus === "Vehicle Arrived At Destination location" && userRecieved === false && (
            <div className="text-center my-4">
              <button className="btn btn-success px-5 py-3 fw-bold" onClick={() => finishBookingUser({ id: order?._id, body: { status: "Job Completed Successfully" } })} disabled={isFinishLoading}>
                {isFinishLoading ? "Finishing..." : "✅ I've Received My Stuff – Finish Booking"}
              </button>
            </div>
          )}

          {/* Payment */}
          <div className="card p-4 mb-4">
            <h4 className="mb-3">Payment Detail</h4>
            <table className="table mb-0">
              <tbody>
                <TableRow label="Total Amount" value={`${totalAmount} PKR`} />
                <TableRow label="Support Helpline" value="03378233366" />
                {orderStatus === "Vehicle not dispatched yet" && (
                  <tr><th scope="row">Cancel Booking</th><td><button className="btn btn-outline-danger btn-sm" onClick={deleteOrderHandler} disabled={isDeleteLoading} id="delete-btn-mybooking">Cancel</button></td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Booking Details */}
          <div className="card p-4 mb-4">
            <h4 className="mb-3">Booking Details</h4>
            <table className="table mb-0">
              <tbody>
                <TableRow label="ID" value={order?._id} />
                <tr><th scope="row">Status</th><td className={String(orderStatus).includes("Job Completed Successfully") ? "greenColor" : "redColor"}><b>{orderStatus}</b></td></tr>
                <TableRow label="Vehicle" value={userAddressInfo?.vehicle} />
                <TableRow label="Booking Type" value={userAddressInfo?.bookingType} />
                <TableRow label="Load Type" value={userAddressInfo?.loadType} />
                <TableRow label="Distance" value={googleMap?.distance} />
                <TableRow label="Date" value={new Date(order?.createdAt).toLocaleString('en-US')} />
                <TableRow label="Notes" value={userAddressInfo?.notes || "No notes"} />
              </tbody>
            </table>
          </div>

          {/* Goods Info */}
          <div className="card p-4 mb-4">
            <h4 className="mb-3">Goods Info</h4>
            <table className="table mb-0">
              <tbody>
                <TableRow label="Type" value={goodsInfo?.goodsType} />
                <TableRow label="Weight" value={`${goodsInfo?.totalWeight} KG`} />
                <TableRow label="Volume" value={`${goodsInfo?.volume} m³`} />
                <TableRow label="Loading Type" value={goodsInfo?.loadingType} />
              </tbody>
            </table>
          </div>

          {/* Google Maps Address */}
          <div className="card p-4 mb-4">
            <h4 className="mb-3">Route Details</h4>
            <table className="table mb-0">
              <tbody>
                <TableRow label="Starting Address" value={googleMap?.origin} />
                <TableRow label="Destination Address" value={googleMap?.destination} />
                <TableRow label="Distance" value={googleMap?.distance} />
              </tbody>
            </table>
          </div>

          {/* Address Info */}
          <div className="card p-4 mb-4">
            <h4 className="mb-3">Address Info</h4>
            <table className="table mb-0">
              <tbody>
                <TableRow label="Name" value={user?.name} />
                <TableRow label="Phone" value={`0${order?.phoneNo}`} />
                <TableRow label="Pickup Address" value={`${userAddressInfo?.startAddress}, ${userAddressInfo?.startCity}`} />
                <TableRow label="Destination Address" value={`${userAddressInfo?.destinationAddress}, ${userAddressInfo?.destinationCity}`} />
              </tbody>
            </table>
          </div>

          {/* Labour Detail */}
          <div className="card p-4 mb-4">
            <h4 className="mb-3">Labour Detail</h4>
            <table className="table mb-0">
              <tbody>
                <TableRow label="Labours at Pickup" value={labour?.noOfLaboursStart || 0} />
                <TableRow label="Labours at Destination" value={labour?.noOfLaboursDestination || 0} />
                <TableRow label="Floors at Pickup" value={labour?.buildingFloorsStart || 0} />
                <TableRow label="Floors at Destination" value={labour?.buildingFloorsDestination || 0} />
                <TableRow label="Cargo Lift (Pickup)" value={labour?.cargoLiftStart ? "Yes" : "No"} />
                <TableRow label="Cargo Lift (Destination)" value={labour?.cargoLiftDestination ? "Yes" : "No"} />
              </tbody>
            </table>
          </div>

          {/* Driver Info */}
          {driver?.driverName && (
            <div className="card p-4 mb-4">
              <h4 className="mb-3">Driver's Detail</h4>
              <table className="table mb-0">
                <tbody>
                  <TableRow label="Name" value={driver?.driverName} />
                  <TableRow label="Vehicle Capacity" value={driver?.driverVehicleCapacity} />
                  <TableRow label="Vehicle Make & Model" value={driver?.driverVehicleMakeModel} />
                  <TableRow label="Vehicle Number" value={driver?.driverVehicleNumber} />
                </tbody>
              </table>
              {driver?.avatar && (
                <div className="mt-4">
                  <img src={driver.avatar} alt="Driver" style={{ width: '100px', height: '100px', borderRadius: '12px', objectFit: 'cover', border: '2px solid var(--color-border)' }} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderDetails;